/*
 * Naked Wardrobe — blog post components
 * --------------------------------------------------------------
 * Upload as assets/nw-blog-components.js and load it (deferred) from
 * sections/main-article.liquid.
 *
 * 1. Live product cards. In the post's HTML an editor writes a plain link:
 *      <a class="nw-product" href="/products/some-handle">Product name</a>
 *    This script fetches /products/some-handle.js and turns the link into a
 *    card with the current image, price, sale price and sold-out badge.
 *    If the product is archived/drafted (404), the card is hidden, so old
 *    posts never show dead products. Without JS it stays a normal link.
 *
 * 2. Carousel arrows. Any .nw-carousel gets prev/next buttons on desktop.
 *    Swiping works with CSS alone.
 *
 * 3. Collection shelves. Leave a shelf's carousel empty and it shows the
 *    first 8 in-stock products of the collection its "See more styles" link
 *    points to, in that collection's sort order, so it changes as the
 *    collection does:
 *      <div class="nw-shelf">
 *        <p class="nw-shelf__head"><span class="nw-shelf__title">Shop the vacation collection</span>
 *          <a class="nw-shelf__more" href="/collections/vacation">See more styles</a></p>
 *        <div class="nw-carousel" aria-label="Shop the vacation collection"></div>
 *      </div>
 *    Put product links inside the carousel instead to hand-pick products.
 */
(() => {
  const S = window.Shopify || {};
  const root = (S.routes && S.routes.root) || '/';
  const currency = (S.currency && S.currency.active) || 'USD';
  const locale = document.documentElement.lang || 'en-US';
  let fmt;
  try {
    fmt = new Intl.NumberFormat(locale, { style: 'currency', currency });
  } catch (e) {
    fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  }
  const money = (cents) => fmt.format(cents / 100);

  const sized = (src, width) => {
    if (!src) return '';
    const url = new URL(src, location.href);
    url.searchParams.set('width', width);
    return url.toString();
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  const chevron = (dir) =>
    `<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="${
      dir === 'prev' ? 'M10.5 2.5 5 8l5.5 5.5' : 'M5.5 2.5 11 8l-5.5 5.5'
    }" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>`;

  /* ---------- Live product cards ---------- */
  async function hydrateCard(card) {
    if (card.dataset.nwState) return;
    card.dataset.nwState = 'loading';
    const link = new URL(card.href, location.href);
    const match = link.pathname.match(/\/products\/([^/]+)/);
    if (!match) return;

    try {
      const res = await fetch(`${root}products/${match[1]}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const product = await res.json();

      const variantId = Number(link.searchParams.get('variant'));
      const variant =
        product.variants.find((v) => v.id === variantId) ||
        product.variants.find((v) => v.available) ||
        product.variants[0];

      const imageSrc =
        (variant && variant.featured_image && variant.featured_image.src) ||
        product.featured_image;
      const price = variant ? variant.price : product.price;
      const compareAt = variant ? variant.compare_at_price : product.compare_at_price;
      const onSale = compareAt && compareAt > price;

      const media = el('span', 'nw-product__media');
      if (imageSrc) {
        const img = el('img');
        img.src = sized(imageSrc, 600);
        img.srcset = `${sized(imageSrc, 400)} 400w, ${sized(imageSrc, 600)} 600w, ${sized(imageSrc, 900)} 900w`;
        img.sizes = '(min-width: 750px) 25vw, 60vw';
        img.alt = product.title;
        img.loading = 'lazy';
        img.width = 600;
        img.height = 800;
        media.append(img);
      }

      const priceEl = el('span', 'nw-product__price');
      if (onSale) {
        priceEl.append(el('s', null, money(compareAt)));
        priceEl.append(el('span', 'nw-product__price--sale', money(price)));
      } else {
        priceEl.textContent = money(price);
      }

      card.textContent = '';
      card.append(media, el('span', 'nw-product__title', product.title), priceEl);

      if (!product.available) {
        card.classList.add('is-sold-out');
        card.append(el('span', 'nw-product__badge', 'Sold out'));
      } else if (onSale) {
        card.append(el('span', 'nw-product__badge', 'Sale'));
      }

      card.classList.add('is-ready');
      card.dataset.nwState = 'ready';
    } catch (err) {
      // Archived, drafted or unpublished products return 404: hide the card.
      card.hidden = true;
      card.dataset.nwState = 'unavailable';
    }

    const track = card.closest('.nw-carousel');
    if (track && track._nwUpdate) requestAnimationFrame(track._nwUpdate);
  }

  function initCards(scope) {
    const cards = scope.querySelectorAll('a.nw-product');
    if (!cards.length) return;
    if (!('IntersectionObserver' in window)) {
      cards.forEach(hydrateCard);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          // Cards scrolled sideways inside a carousel never "intersect", so
          // load the whole carousel as soon as any card in it is near view.
          const track = entry.target.closest('.nw-carousel');
          const batch = track ? track.querySelectorAll('a.nw-product') : [entry.target];
          batch.forEach((card) => {
            io.unobserve(card);
            hydrateCard(card);
          });
        });
      },
      { rootMargin: '600px 0px' }
    );
    cards.forEach((card) => io.observe(card));
  }

  /* ---------- Carousel arrows ---------- */
  function initCarousel(track) {
    if (track._nwUpdate) return;

    const wrap = el('div', 'nw-carousel-wrap');
    if (track.classList.contains('nw-breakout')) {
      track.classList.remove('nw-breakout');
      wrap.classList.add('nw-breakout');
    }
    track.before(wrap);
    wrap.append(track);

    if (!track.hasAttribute('tabindex')) track.tabIndex = 0;
    if (!track.hasAttribute('role')) track.setAttribute('role', 'region');
    if (!track.hasAttribute('aria-label')) track.setAttribute('aria-label', 'Carousel');

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const makeButton = (dir, label) => {
      const btn = el('button', `nw-carousel__btn nw-carousel__btn--${dir}`);
      btn.type = 'button';
      btn.setAttribute('aria-label', label);
      btn.innerHTML = chevron(dir);
      btn.addEventListener('click', () => {
        track.scrollBy({
          left: (dir === 'prev' ? -1 : 1) * track.clientWidth * 0.9,
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
        });
      });
      return btn;
    };
    const prev = makeButton('prev', 'Previous');
    const next = makeButton('next', 'Next');
    wrap.append(prev, next);

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max - 2;
      wrap.classList.toggle('is-static', max <= 2);
    };
    track._nwUpdate = update;
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    track.querySelectorAll('img').forEach((img) => img.addEventListener('load', update, { once: true }));
    update();
  }

  /* ---------- Collection-driven shelves ---------- */
  // A .nw-shelf whose carousel has no product links fills itself from the
  // collection its "See more styles" link points to, in that collection's
  // own sort order. Sold-out products are skipped.
  const SHELF_SIZE = 8;

  async function fillShelf(shelf) {
    const track = shelf.querySelector('.nw-carousel');
    const more = shelf.querySelector('a.nw-shelf__more');
    if (!track || !more || track.querySelector('a.nw-product')) return;
    const match = new URL(more.href, location.href).pathname.match(/\/collections\/([^/]+)/);
    if (!match) return;

    try {
      const res = await fetch(`${root}collections/${match[1]}/products.json?limit=${SHELF_SIZE * 2}`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { products = [] } = await res.json();
      products
        .filter((p) => p.variants && p.variants.some((v) => v.available))
        .slice(0, SHELF_SIZE)
        .forEach((p) => {
          const card = el('a', 'nw-product', p.title);
          card.href = `${root}products/${p.handle}`;
          track.append(card);
        });
    } catch (err) {
      // Collection missing or unreachable: leave the shelf empty; the
      // "See more styles" link still works.
    }
    if (!track.querySelector('a.nw-product')) shelf.hidden = true;
    if (track._nwUpdate) requestAnimationFrame(track._nwUpdate);
    initCards(shelf);
  }

  function init() {
    const scope = document.querySelector('.article-template') || document;
    scope.querySelectorAll('.nw-carousel').forEach(initCarousel);
    initCards(scope);
    scope.querySelectorAll('.nw-shelf').forEach(fillShelf);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
